import { useState, useEffect } from "react";
import { JobPost, CRMInteraction, CRMPerson } from "../types";
import Stepper from "@/components/Stepper";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import { toDateFormat } from "@/lib/dateHelpers";
import Icon from "@/components/Icon";
import {
  getIcon,
  getLinkDescriptionForJobInteractions,
  groupInteractions,
  handleSummary
} from "@/components/interactions/_utils";
import { InteractionMode, Interaction } from "@/components/interactions/types";
import Tag from "@/components/Tag";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import { useApolloClient, useQuery } from "@apollo/client";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { BackofficePerson } from "@/types";
import { useNavigate, useLocation } from "react-router-dom";
import { showSnackbar } from "@/lib/snackbarUtils";
import EmptyState from "@/components/EmptyState";
import SectionHeading from "@/components/SectionHeading";
import useCapabilities, { PERMISSION, useCanManageInteractionsBool } from "@/hooks/useCapabilities";
import { JOB_POST_WITH_INTERACTIONS_QUERY, updateCache } from "../queries";
import InteractionsPageSkeleton from "./InteractionsPageSkeleton";

interface ViewJobInteractionsProps {
  jobPost: JobPost;
}

const interactionTypeOptions = [
  { value: "ALL", label: "Show All" },
  { value: InteractionMode.PHONE, label: "Phone Call" },
  { value: InteractionMode.VIDEO, label: "Video Chat" },
  { value: InteractionMode.EMAIL, label: "Email" },
  { value: InteractionMode.IN_PERSON, label: "In-Person" },
  { value: InteractionMode.SMS, label: "Text Message" },
  { value: InteractionMode.LETTER, label: "Letter" },
  { value: InteractionMode.LIVE_CHAT, label: "Live Chat" }
];

interface InteractionGroupProps {
  monthYear: string;
  interactions: CRMInteraction[];
  onPersonClick: (participant: CRMPerson) => void;
}

const InteractionGroup = ({ monthYear, interactions, onPersonClick }: InteractionGroupProps) => {
  const [isExpanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isSectionRoute = location.pathname.includes("/sections/");

  const stepsList = interactions.map((interaction) => {
    const {
      mode,
      owner,
      lastMessage,
      messagesCount,
      categories,
      interactionDate,
      createdAt,
      participants,
      showAiSummary
    } = interaction;

    const summary = handleSummary(interaction);

    const { isPrivate, haveAccess, content } = lastMessage;
    const interactionMode = mode as InteractionMode;
    const icon = getIcon(interactionMode);
    const methodLabel = interactionTypeOptions.find((opt) => opt.value === mode)?.label || mode;
    const hasAccessToInteraction = !isPrivate || (isPrivate && haveAccess);
    const displayDate = interactionDate || createdAt;

    return {
      headline: (
        <div className="flex flex-wrap gap-x-1 items-center relative">
          {participants.map((participant: CRMPerson, index: number) => (
            <div
              key={index}
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (participant.pgaId) {
                  onPersonClick(participant);
                }
              }}
            >
              <span className={participant.pgaId ? "text-sys-brand-primary hover:underline" : ""}>
                {participant.firstName} {participant.lastName}
              </span>
              {index < participants.length - 1 && ", "}
            </div>
          ))}
          <span>{getLinkDescriptionForJobInteractions(interactionMode)}</span>
          <span className="text-sys-brand-primary">{methodLabel}</span>
          {owner?.name && (
            <>
              <span>with</span>
              <span>{owner?.name}</span>
            </>
          )}
          {messagesCount > 1 && <span>({messagesCount} messages)</span>}
          {isPrivate && <Icon name="lock" size={24} />}
        </div>
      ),
      supportingText: toDateFormat(new Date(displayDate).toISOString().split("T")[0]) || "",
      icon,
      children: (
        <>
          {hasAccessToInteraction && (
            <>
              <ul className="flex flex-wrap gap-2 items-center mt-3">
                {categories.map((category: string, index: number) => (
                  <li key={index}>
                    <Tag tagType="secondary">{category}</Tag>
                  </li>
                ))}
              </ul>
              <p className="mt-3">{showAiSummary ? summary : content}</p>
            </>
          )}
        </>
      )
    };
  });

  const handleStepClick = (stepIndex: number) => {
    const interaction = interactions[stepIndex];
    const { lastMessage } = interaction;
    const { isPrivate, haveAccess } = lastMessage;
    const hasAccessToInteraction = !isPrivate || (isPrivate && haveAccess);

    if (!hasAccessToInteraction) {
      showSnackbar("You don't have access to this interaction");
      return;
    }

    const currentPath = location.pathname;
    const pathParts = currentPath.split("/");
    const jobPostId = pathParts[pathParts.length - 2];
    const sectionId = isSectionRoute ? pathParts[2] : null;

    if (isSectionRoute && sectionId) {
      navigate(`/sections/${sectionId}/jobs/job-post/${jobPostId}/interactions/${interaction.id}`);
    } else {
      navigate(`/jobs/job-post/${jobPostId}/interactions/${interaction.id}`);
    }
  };

  return (
    <>
      <div className="pt-8 pb-3 flex items-center gap-2 cursor-pointer" onClick={() => setExpanded(!isExpanded)}>
        {monthYear}
        <Icon name={isExpanded ? "expand_less" : "expand_more"} />
      </div>
      {isExpanded && <Stepper variant="solid" stepsList={stepsList} onStepClick={handleStepClick} />}
    </>
  );
};

export default function ViewJobInteractions({ jobPost }: ViewJobInteractionsProps) {
  const [isApplicantDetailsDialogOpen, setIsApplicantDetailsDialogOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState<BackofficePerson | null>(null);
  const [selectedApplicantName, setSelectedApplicantName] = useState<string | null>(null);
  const { showSidebar } = useTopActionBar();
  const { hasPermission } = useCapabilities();
  const canManageJobs = hasPermission(PERMISSION.manageJobPosts);
  const canManageInteractions = useCanManageInteractionsBool();
  const location = useLocation();
  const navigate = useNavigate();
  const openCreateInteraction = showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, {
    defaultJobPostId: jobPost.id
  });

  const [interactions, setInteractions] = useState<any[]>([]);

  const { data, loading } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_INTERACTIONS_QUERY, {
    variables: { jobPostId: jobPost?.id },
    skip: !jobPost?.id,
    fetchPolicy: "cache-first"
  });

  const { cache } = useApolloClient();

  useEffect(() => {
    if (data) {
      setInteractions(data.jobPost.interactions || []);
      updateCache(cache, data.jobPost.id, "interactions", data.jobPost.interactions);
    } else {
      setInteractions([]);
    }
  }, [cache, data]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenDialog = searchParams.get("openCreateDialog") === "true";

    if (shouldOpenDialog) {
      showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, {
        defaultJobPostId: jobPost.id
      })();
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("openCreateDialog");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [location.search, navigate, showSidebar, jobPost.id]);

  const { loading: _peopleLoading } = useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: {
        search: selectedApplicantName ? `${selectedApplicantName}` : ""
      },
      first: 1
    },
    skip: !selectedApplicantName,
    onCompleted: (data) => {
      if (data?.people?.nodes[0]) {
        setSelectedApplicant(data.people.nodes[0]);
      }
    }
  });

  // Map interactions to ensure they have a valid date (using createdAt as fallback)
  const processedInteractions = interactions.map((interaction) => ({
    ...interaction,
    interactionDate: interaction.interactionDate || interaction.createdAt
  }));
  const interactionsMap = groupInteractions(processedInteractions as unknown as Interaction[]);

  const handlePersonClick = (participant: CRMPerson) => {
    setSelectedApplicant(participant as BackofficePerson);
    setSelectedApplicantName(participant.firstName + " " + participant.lastName);
    setIsApplicantDetailsDialogOpen(true);
  };

  const handleClosePersonDetails = () => {
    setIsApplicantDetailsDialogOpen(false);
    setSelectedApplicant(null);
  };

  if (loading) {
    return <InteractionsPageSkeleton />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="[&>div]:pb-0">
        <SectionHeading
          heading="Interactions"
          body="View tracked conversations, outreach, and other touchpoints related to this job post."
          actions={
            canManageJobs && canManageInteractions
              ? [
                  {
                    label: "Add Interaction",
                    onClick: openCreateInteraction,
                    testId: "add-interaction",
                    icon: "add_comment"
                  }
                ]
              : []
          }
        />
      </div>
      {/* TODO: Add back in when we have the metrics */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label="Total Interactions"
          value={totalInteractions.toString()}
        />
        <MetricCard
          label="Total Messages"
          value={totalMessages.toString()}
        />
        <MetricCard
          label="Total Participants"
          value={totalParticipants.toString()}
        />
      </div> */}

      {interactions.length > 0 ? (
        <ul className="flex flex-col">
          {Object.entries(interactionsMap).map(([monthYear, monthInteractions], i) => (
            <li key={i} data-testid={`interactions-month-group-${i}`}>
              <InteractionGroup
                monthYear={monthYear}
                interactions={monthInteractions as unknown as CRMInteraction[]}
                onPersonClick={handlePersonClick}
              />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          title="No Interactions"
          caption="There are no interactions associated with this job."
          iconName="3p"
        />
      )}

      <ApplicantDetailsDialog
        applicant={selectedApplicant}
        open={isApplicantDetailsDialogOpen}
        onClose={handleClosePersonDetails}
      />
    </div>
  );
}
